/**
 * Created by staffanrantzow on 05/04/16.
 */

import axios from 'axios';
import CustomerEntity from './customer-entity';
import AddressEntity from '../address/address-entity';
import {APIResult} from '../http';
import { proxyCounterpart } from '../../scripts/proxyCounterpart';
import PendingRequestEntity from '../entities/pending-request-entity';
import { billingEmailType } from './customer-constants';

class CustomerService {
  hydrate (customer) {
    const emails = customer.attributes.billing_emails;
    const primaryBillingEmail = emails[0] || '';
    const secondaryBillingEmail = emails[1] || '';

    return new CustomerEntity(
      customer.id,
      customer.attributes.contact,
      customer.attributes.email,
      customer.attributes.name,
      customer.attributes.phone,
      primaryBillingEmail,
      secondaryBillingEmail,
      customer.attributes.user_id,
      customer.attributes.vatnr,
      new AddressEntity(
        customer.attributes.city,
        customer.attributes.co,
        customer.attributes.country,
        customer.attributes.street,
        customer.attributes.zip
      ),
      customer.attributes.pay_terms,
      customer.attributes.message_type || 'email',
      customer.attributes.e_invoice_address || '',
      customer.attributes.po_number || ''
    );
  }

  hydrateCollection (data) {
    var customers = [];

    for (let customer of data) {
      customers.push(this.hydrate(customer));
    }

    return customers;
  }

  extractParams (params) {
    return {
      data: {
        attributes: {
          city: params.city,
          co: params.co,
          contact: params.contact,
          country: params.country,
          email: params.email,
          name: params.name,
          phone: params.phone,
          send_to_email: params.sendToEmail,
          send_to_email_secondary: params.sendToEmailSecondary,
          street: params.street,
          user_id: params.userId,
          vatnr: params.vatNr,
          zip: params.zip,
          pay_terms: params.payTerms
        }
      }
    };
  }

  getError (error) {
    let status = (error.response || { status: 200 }).status;

    if (status === 422) {
      let errorMessages = [];
      for (let error of error.response.data.errors) {
        if (error.detail.hasOwnProperty('errors')) {
          if (error.detail.errors.hasOwnProperty('country') &&
            error.detail.errors.country === 'required') {
            errorMessages.push(proxyCounterpart('COUNTRY_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('vatnr') &&
            error.detail.errors.vatnr === 'requiredunless:country,sweden') {
            errorMessages.push(proxyCounterpart('ORG_NR_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('contact') &&
            error.detail.errors.contact === 'max:30') {
            errorMessages.push(proxyCounterpart('CONTACT_MAX_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('id') &&
            error.detail.errors.id === 'notfound:invoice,company_id,pre_report,1') {
            errorMessages.push(proxyCounterpart('CANT_DELETE_COMPANY_WITH_PREREPORT_INVOICES'));
          }
          if (error.detail.errors.hasOwnProperty('send_to_email') &&
            error.detail.errors.send_to_email === 'email') {
            errorMessages.push(proxyCounterpart('INVALID_EMAIL'));
          }
          if (error.detail.errors.hasOwnProperty('phone') &&
            error.detail.errors.phone === 'phonenumber') {
            errorMessages.push(proxyCounterpart('INVALID_PHONE_NUMBER'));
          }
        }
      }

      if (errorMessages.length > 0) {
        return errorMessages;
      }
    }

    return false;
  }

  getById (id) {
    let pendingRequest = new PendingRequestEntity();
    pendingRequest.promise = axios.get('backend://user/companies/' + id, {
      cancelToken: new axios.CancelToken(function executor (c) {
        pendingRequest.cancel = c;
      })
    })
      .then(
        (response) => this.hydrate(response.data.data)
      );

    return pendingRequest;
  }

  get (userId, page, size, searchValue) {
    let pendingRequest = new PendingRequestEntity();
    pendingRequest.promise = axios.get('backend://users/' + userId + '/companies', {
      params: {
        'page[number]': page || 1,
        'sort': 'name',
        'page[size]': size || 20,
        'filter[name][*]': searchValue || ''
      },
      cancelToken: new axios.CancelToken(function executor (c) {
        pendingRequest.cancel = c;
      })
    })
      .then((response) => {
        var customers = this.hydrateCollection(response.data.data);
        /*
      response.data.meta = {
        pagination: {
          count: 6,
          current_page: 1,
          per_page: 25,
          total: 6,
          total_pages: 1
        }
      };
      */
        return new APIResult(customers, response.data.meta);
      });

    return pendingRequest;
  }

  update (id, params) {
    return axios.put('backend://user/companies/' + id, this.extractParams(params))
      .then((response) => { return this.hydrate(response.data.data); });
  }

  updateBillingEmails (id, params) {
    const requestData = { data: { attributes: { } } };
    const emails = [];

    if (params.sendToEmail) {
      let firstEmail = {
        type_id: billingEmailType,
        email: params.sendToEmail
      };
      emails.push(firstEmail);
    }

    if (params.sendToEmailSecondary) {
      let secondaryEmail = {
        type_id: billingEmailType,
        email: params.sendToEmailSecondary
      };
      emails.push(secondaryEmail);
    }

    requestData.data.attributes = { emails };
    return axios.patch(`backend://user/companies/${id}/emails`, requestData);
  }

  post (params) {
    let p = this.extractParams(params);
    p.user_id = 1; // TEMP SOLUTION

    return axios.post('backend://user/companies', p)
      .then((response) => this.hydrate(response.data.data));
  }

  delete (id) {
    return axios.delete('backend://user/companies/' + id);
  }

  updateMessaging (data) {
    const companyId = data.company_id;
    const type = data.message_type;
    const attributes = {
      message_type: type
    };

    if (type === 'email') {
      attributes.send_to_email = data.send_to_email;
    }

    if (type === 'sms') {
      attributes.phone = data.company_phone;
    }

    if (type === 'e-invoice') {
      attributes.po_number = data.po_number;
      attributes.e_invoice_address = data.e_invoice_address;
    }

    const payload = {
      data: { attributes }
    };

    return axios.put('backend://user/companies/' + companyId, payload);
  }
}

export default new CustomerService();



/** WEBPACK FOOTER **
 ** ./src/frilans-finans/customers/customer-service.js
 **/